import {
  Actions,
  AmountCell,
  Badge,
  CheckCircleIcon,
  DateCell,
  DownloadInvoice,
  EyeIcon,
  IdCell,
  Image,
  NameCell,
  PDF_file_icon,
  PhoneCell,
  PrinterIcon,
  Tooltip,
  XCircleIcon,
} from "@";

const isPdfUrl = (url) =>
  typeof url === "string" &&
  url.toLowerCase().split(/[?#]/)[0].endsWith(".pdf");

// Receipt thumbnail — feature-specific because of the PDF/image switch
// and the bank-transfer-only visibility rule. Stays inline here.
const ReceiptCell = ({ url, paymentType }) => {
  if (paymentType !== 1 || !url) {
    return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
  }
  return (
    <a
      href={isPdfUrl(url) ? url : `#`}
      target={isPdfUrl(url) ? "_blank" : undefined}
      rel="noopener noreferrer"
      title={isPdfUrl(url) ? "فتح إشعار الحوالة (PDF)" : "فتح صورة الإشعار"}
      className="inline-flex h-10 w-12 items-center justify-center overflow-hidden rounded-lg bg-gray-50 ring-1 ring-gray-100 transition hover:ring-purple-200 dark:bg-gray-800 dark:ring-gray-700 dark:hover:ring-purple-700/60"
    >
      {isPdfUrl(url) ? (
        <img width={28} src={PDF_file_icon} alt="PDF" />
      ) : (
        <Image src={url} />
      )}
    </a>
  );
};

const ORDER_STATUS_MAP = {
  0: { title: "مؤكد", color: "green" },
  1: { title: "غير مؤكد", color: "default" },
  2: { title: "يحتاج موافقة", color: "yellow" },
  3: { title: "مرفوض", color: "red" },
};

export const TableActions = ({ row, show, handelPaid, rejectBankTransfer }) => {
  const renderTooltip = (id, title) => <Tooltip title={title} id={id} />;

  const renderIcon = (id, IconComponent, onClick, title) => (
    <>
      <span
        data-tooltip-target={id}
        data-tooltip-placement="right"
        className="icon-style"
        onClick={onClick}
      >
        <IconComponent className="w-6" />
      </span>
      {renderTooltip(id, title)}
    </>
  );

  return (
    <Actions notShow row={row} notDelete notEdit>
      {renderIcon(`show-tooltip-${row.id}`, EyeIcon, () => show(row), "عرض ")}

      {row.orderStatus === 2 &&
        row.paymentType === 1 &&
        renderIcon(
          `bank-tooltip-${row.id}`,
          XCircleIcon,
          () => rejectBankTransfer(row.id),
          "رفض الحوالة البنكية",
        )}

      {!row.isPaid &&
        renderIcon(
          `status-tooltip-${row.id}`,
          CheckCircleIcon,
          () => handelPaid(row.id),
          "مدفوعة",
        )}

      {row.bankTransferFullUrl && row.paymentType === 1 && (
        <>
          <a
            href={row.bankTransferFullUrl}
            target="_blank"
            data-tooltip-target={`print-tooltip-${row.id}`}
            data-tooltip-placement="right"
            className="icon-style"
          >
            <PrinterIcon className="w-6" />
          </a>
          {renderTooltip(`print-tooltip-${row.id}`, " إشعار الحوالة البنكية")}
        </>
      )}

      <DownloadInvoice
        id={row.id}
        targetId={row.id}
        title="تحميل ايصال التبرع"
      />
    </Actions>
  );
};

export const tableColumns = [
  {
    id: "bankTransferFullUrl",
    header: "الايصال",
    size: 80,
    cell: ({ row }) => (
      <ReceiptCell
        url={row.original.bankTransferFullUrl}
        paymentType={row.original.paymentType}
      />
    ),
  },
  {
    id: "id",
    header: "رقم العملية",
    size: 100,
    cell: ({ row }) => <IdCell value={row.original.id} />,
  },
  {
    id: "userName",
    header: "اسم المتبرع",
    size: 180,
    cell: ({ row }) => <NameCell name={row.original.userName} />,
  },
  {
    id: "phoneNumber",
    header: "رقم الهاتف",
    size: 140,
    cell: ({ row }) => <PhoneCell phone={row.original.phoneNumber} />,
  },
  {
    id: "totalAmount",
    header: "المبلغ",
    size: 80,
    cell: ({ row }) => (
      <AmountCell value={row.original.totalAmount} tone="emerald" />
    ),
  },
  {
    id: "donationDate",
    header: "تاريخ التبرع",
    size: 130,
    cell: ({ row }) => <DateCell value={row.original.donationDate} />,
  },
  {
    id: "paymentType",
    header: "وسيلة التبرع",
    size: 100,
    cell: ({ row }) => (
      <Badge
        title={row.original.paymentType === 0 ? "بطاقة" : "تحويل بنكي"}
        color={row.original.paymentType === 0 ? "default" : "dark"}
      />
    ),
  },
  {
    id: "isPaid",
    header: "حالة الدفع",
    size: 100,
    cell: ({ row }) => (
      <Badge
        title={row.original.isPaid ? "مدفوعة" : "غير مدفوعة"}
        color={row.original.isPaid ? "green" : "red"}
      />
    ),
  },
  {
    id: "orderStatus",
    header: "حالة الطلب",
    size: 100,
    cell: ({ row }) => {
      const { title, color } = ORDER_STATUS_MAP[row.original.orderStatus] || {};
      return title ? <Badge title={title} color={color} /> : null;
    },
  },
];
